import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { categories, stats } from "@/mocks/site";
import { topics } from "@/mocks/topics";

const hotTags = ["年度盘点", "名场面", "TOP榜", "合约风波", "舞台改编", "二创合集"];

export default function HomeHero() {
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);

  const results = useMemo(() => {
    const q = query.trim();
    if (!q) return [];
    return topics
      .filter(
        (t) =>
          t.title.includes(q) ||
          t.summary.includes(q) ||
          t.category.includes(q) ||
          t.tags.some((tag) => tag.includes(q))
      )
      .slice(0, 5);
  }, [query]);

  return (
    <section className="relative w-full min-h-[560px] md:min-h-[680px] flex items-center overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=abstract%20warm%20stage%20with%20sweeping%20amber%20and%20coral%20light%20beams%2C%20soft%20bokeh%20particles%2C%20dark%20cream%20gradient%20background%2C%20cinematic%20minimal%20artistic%20poster%2C%20no%20people%2C%20no%20text&width=1600&height=1000&seq=gt-hero-bg&orientation=landscape"
        alt="瓜田黑料首屏背景：抽象舞台灯光与暖色光晕"
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-primary-950/80 via-primary-950/70 to-primary-950/85" />
      <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/40 via-transparent to-transparent" />

      <div className="relative z-10 w-full px-4 md:px-10 py-28 md:py-32">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background-50/15 backdrop-blur text-background-100 text-xs tracking-wide">
            <i className="ri-plant-line text-accent-400" />
            年度盘点 · TOP榜 · 名场面合集
          </span>

          <h1 className="mt-6 font-heading font-black text-3xl sm:text-4xl md:text-6xl leading-tight text-background-50">
            瓜田黑料
            <span className="block mt-2 text-xl sm:text-2xl md:text-3xl font-bold text-accent-300">
              年度盘点 TOP 榜 · 名场面全收录
            </span>
          </h1>

          <p className="mt-5 max-w-2xl text-sm md:text-base leading-relaxed text-background-200/90">
            快速浏览今年最值得吃的瓜，补课必备目录。年度盘点、TOP榜、合集整理、人物志、名场面、经典案例全收录，一篇看完所有高能名场面。
          </p>

          <div className="relative mt-8 max-w-xl">
            <div className="flex items-center gap-2 bg-background-50 rounded-full pl-5 pr-2 py-2">
              <i className="ri-search-line text-foreground-400 text-lg" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => window.setTimeout(() => setFocused(false), 160)}
                placeholder="搜索话题、名场面或榜单"
                className="flex-1 bg-transparent outline-none text-sm text-foreground-900 placeholder:text-foreground-400 py-1.5"
              />
              <Link
                to="/rankings"
                className="hidden sm:inline-flex items-center gap-1 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                去看榜单
              </Link>
            </div>

            {focused && query.trim() !== "" && (
              <div className="absolute left-0 right-0 mt-2 rounded-lg bg-background-50 border border-background-200 overflow-hidden z-20">
                {results.length > 0 ? (
                  results.map((item) => (
                    <Link
                      key={item.slug}
                      to={`/topic/${item.slug}`}
                      className="flex items-start gap-3 px-4 py-3 hover:bg-background-100 transition-colors border-b border-background-100 last:border-0"
                    >
                      <i className="ri-file-list-3-line text-primary-500 mt-0.5" />
                      <span>
                        <span className="block text-sm text-foreground-900">{item.title}</span>
                        <span className="block text-xs text-foreground-500 mt-0.5">
                          {item.category} · 热度 {item.heat}
                        </span>
                      </span>
                    </Link>
                  ))
                ) : (
                  <p className="px-4 py-4 text-sm text-foreground-500">
                    没有找到相关话题，试试「年度盘点」「名场面」等关键词。
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <span className="text-xs text-background-200/70 mr-1">热门：</span>
            {hotTags.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => setQuery(tag)}
                className="px-3 py-1 rounded-full bg-background-50/10 hover:bg-background-50/20 text-background-100 text-xs transition-colors whitespace-nowrap"
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {stats.map((stat) => (
            <div key={stat.label} className="border-l-2 border-accent-500/70 pl-4">
              <p className="font-heading font-bold text-2xl md:text-3xl text-background-50">
                {stat.value}
                <span className="text-sm font-normal text-background-200/70 ml-1">{stat.suffix}</span>
              </p>
              <p className="mt-1 text-xs md:text-sm text-background-200/70">{stat.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap gap-2">
          {categories.slice(0, 4).map((cat) => (
            <Link
              key={cat.slug}
              to="/rankings"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-background-50/10 backdrop-blur text-background-100 text-sm hover:bg-background-50/20 transition-colors whitespace-nowrap"
            >
              <i className={cat.icon} />
              {cat.name}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}